"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { CheckCircle, AlertCircle, Info, X } from "lucide-react"

interface NotificationProps {
  message: string
  type?: "success" | "error" | "info"
  duration?: number
  onClose: () => void
}

const Notification = ({ message, type = "error", duration = 5000, onClose }: NotificationProps) => {
  const [isVisible, setIsVisible] = useState(true)
  const [progress, setProgress] = useState(100)
  // Usamos un ref en lugar de state para almacenar el id del intervalo
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Envolvemos handleClose en useCallback para que su identidad sea estable y podamos incluirlo en las dependencias
  const handleClose = useCallback(() => {
    setIsVisible(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    // Agrega un pequeño retraso para permitir que se complete la animación
    setTimeout(() => {
      onClose()
    }, 300)
  }, [onClose])

  useEffect(() => {
    // Iniciar la barra de progreso
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(interval)
          return 0
        }
        return prev - 100 / (duration / 100)
      })
    }, 100)
    intervalRef.current = interval

    // Al finalizar el tiempo, cierra la notificación
    const timeout = setTimeout(() => {
      handleClose()
    }, duration)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [duration, handleClose]) // Ahora se incluye handleClose en las dependencias

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />
      case "info":
        return <Info className="h-5 w-5 text-blue-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-red-500" />
    }
  }

  const getBgColor = () => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200"
      case "error":
        return "bg-red-50 border-red-200"
      case "info":
        return "bg-blue-50 border-blue-200"
      default:
        return "bg-red-50 border-red-200"
    }
  }

  const getProgressColor = () => {
    switch (type) {
      case "success":
        return "bg-green-500"
      case "error":
        return "bg-red-500"
      case "info":
        return "bg-blue-500"
      default:
        return "bg-red-500"
    }
  }

  return (
    <div
      className={`fixed top-4 right-4 z-50 max-w-md transition-all duration-300 transform ${
        isVisible ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
      }`}
    >
      <div className={`rounded-lg shadow-lg border p-4 ${getBgColor()}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">{getIcon()}</div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-800">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              onClick={handleClose}
            >
              <span className="sr-only">Cerrar</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="mt-3 w-full bg-gray-200 rounded-full h-1">
          <div
            className={`h-1 rounded-full ${getProgressColor()}`}
            style={{ width: `${progress}%`, transition: "width 100ms linear" }}
          ></div>
        </div>
      </div>
    </div>
  )
}

export default Notification
